import { CREATURE_BY_ID, creatureTone, TONE_COLORS } from "@eldrun/content";
import { Color, DynamicDrawUsage, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, PlaneGeometry, type Object3D } from "three";
import { createHealthBarMaterial } from "../render/shaders/health-bar.ts";
import { gameColor } from "../render/style/roles.ts";
import type { CreatureSnapshot } from "./creature-layer.ts";

/** Pool de barras (doc 05: 8 instâncias, um draw). */
export const BAR_POOL = 8;
/** "Olhar revela": criatura a até 30 m e a até ~1,2 m da linha do olhar (cresce com a distância). */
export const LOOK_RANGE = 30;
const LOOK_RADIUS = 1.2;
const LOOK_RADIUS_PER_M = 0.03;
/** Recém-atingida por qualquer jogador: barra por 3 s, a até 60 m. */
export const HIT_SHOW_MS = 3000;
const HIT_RANGE = 60;

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Quais criaturas ganham barra: as olhadas primeiro (pela distância ao longo do olhar), depois as
 * recém-atingidas (pela distância), até `BAR_POOL`. `forward` deve estar normalizado.
 */
export function selectBars(creatures: readonly CreatureSnapshot[], eye: Vec3, forward: Vec3, hitAt: ReadonlyMap<string, number>, now: number): CreatureSnapshot[] {
  const looked: { c: CreatureSnapshot; k: number }[] = [];
  const hit: { c: CreatureSnapshot; k: number }[] = [];
  for (const c of creatures) {
    // Chefes têm a barra no alto da tela (doc 01), não a flutuante.
    if (c.state === "morta" || c.hpMax <= 0 || CREATURE_BY_ID.get(c.type)?.boss) continue;
    const height = CREATURE_BY_ID.get(c.type)?.height ?? 0.6;
    const dx = c.x - eye.x;
    const dy = c.y + height / 2 - eye.y;
    const dz = c.z - eye.z;
    const along = dx * forward.x + dy * forward.y + dz * forward.z;
    if (along > 0 && along <= LOOK_RANGE) {
      const px = dx - along * forward.x;
      const py = dy - along * forward.y;
      const pz = dz - along * forward.z;
      if (Math.hypot(px, py, pz) <= LOOK_RADIUS + along * LOOK_RADIUS_PER_M) {
        looked.push({ c, k: along });
        continue;
      }
    }
    const t = hitAt.get(c.id);
    const d = Math.hypot(dx, dy, dz);
    if (t !== undefined && now - t <= HIT_SHOW_MS && d <= HIT_RANGE) hit.push({ c, k: d });
  }
  looked.sort((a, b) => a.k - b.k);
  hit.sort((a, b) => a.k - b.k);
  return [...looked, ...hit].slice(0, BAR_POOL).map((e) => e.c);
}

interface BarState {
  fill: number;
  chip: number;
  /** Até quando a faixa do dano recente fica parada antes de descer. */
  chipHoldUntil: number;
  alpha: number;
}

const fraction = (c: CreatureSnapshot) => Math.max(0, Math.min(1, c.hp / Math.max(1, c.hpMax)));
/**
 * Cor da barra (F75): o tom da criatura pela paleta (fauna neutra, monstro laranja, Guardião vermelho), o
 * mesmo do corpo: a fauna que ataca (`hostile`) fica laranja.
 */
export function barTone(type: string, hostile = false): string {
  return gameColor(TONE_COLORS[creatureTone(CREATURE_BY_ID.get(type)?.archetype ?? "assombracao", hostile)]);
}

const barHeight = (c: CreatureSnapshot) => c.y + (CREATURE_BY_ID.get(c.type)?.height ?? 0.6) + 0.35;

/**
 * Barras de vida acima das criaturas. O preenchimento persegue a fração de vida em ~0,1 s; a faixa
 * clara do dano recente espera 0,4 s e desce devagar; a barra entra e sai com fade.
 */
export class CreatureBars {
  private readonly mesh: Mesh;
  private readonly geometry: InstancedBufferGeometry;
  private readonly center: InstancedBufferAttribute;
  private readonly fill: InstancedBufferAttribute;
  private readonly chip: InstancedBufferAttribute;
  private readonly alpha: InstancedBufferAttribute;
  private readonly color: InstancedBufferAttribute;
  private readonly elite: InstancedBufferAttribute;
  private readonly tmp = new Color();
  private readonly bars = new Map<string, BarState>();
  private readonly hitAt = new Map<string, number>();

  constructor(root: Object3D) {
    const plane = new PlaneGeometry(1, 1);
    this.geometry = new InstancedBufferGeometry();
    this.geometry.setIndex(plane.getIndex());
    this.geometry.setAttribute("position", plane.getAttribute("position"));
    this.geometry.setAttribute("uv", plane.getAttribute("uv"));
    const attr = (size: number) => new InstancedBufferAttribute(new Float32Array(BAR_POOL * size), size).setUsage(DynamicDrawUsage);
    this.center = attr(3);
    this.fill = attr(1);
    this.chip = attr(1);
    this.alpha = attr(1);
    this.color = attr(3);
    this.elite = attr(1);
    this.geometry.setAttribute("aCenter", this.center);
    this.geometry.setAttribute("aFill", this.fill);
    this.geometry.setAttribute("aChip", this.chip);
    this.geometry.setAttribute("aAlpha", this.alpha);
    this.geometry.setAttribute("aColor", this.color);
    this.geometry.setAttribute("aElite", this.elite);
    this.geometry.instanceCount = 0;
    this.mesh = new Mesh(this.geometry, createHealthBarMaterial());
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
    root.add(this.mesh);
  }

  /** Alguém acertou esta criatura (evento `dano` com o id dela). `now` em ms. */
  markHit(id: string, now: number): void {
    this.hitAt.set(id, now);
  }

  /** `now` em ms; `dt` em s. */
  update(creatures: readonly CreatureSnapshot[], eye: Vec3, forward: Vec3, now: number, dt: number): void {
    const byId = new Map(creatures.map((c) => [c.id, c]));
    const active = new Set<string>();
    for (const c of selectBars(creatures, eye, forward, this.hitAt, now)) {
      active.add(c.id);
      if (!this.bars.has(c.id)) this.bars.set(c.id, { fill: fraction(c), chip: fraction(c), chipHoldUntil: 0, alpha: 0 });
    }
    // As selecionadas ocupam o pool antes das que estão sumindo.
    const order = [...this.bars.keys()].sort((a, b) => Number(active.has(b)) - Number(active.has(a)));
    let i = 0;
    for (const id of order) {
      const bar = this.bars.get(id) as BarState;
      const c = byId.get(id);
      const on = c !== undefined && active.has(id);
      bar.alpha = Math.max(0, Math.min(1, bar.alpha + (on ? dt : -dt) * 6));
      if (!c || (!on && bar.alpha <= 0)) {
        this.bars.delete(id);
        continue;
      }
      const target = fraction(c);
      if (target < bar.fill - 1e-4) bar.chipHoldUntil = now + 400;
      bar.fill += (target - bar.fill) * (1 - Math.exp(-dt * 20));
      bar.chip = now < bar.chipHoldUntil ? Math.max(bar.chip, bar.fill) : Math.max(bar.fill, bar.chip - dt * 0.6);
      if (i >= BAR_POOL) continue;
      this.center.setXYZ(i, c.x, barHeight(c), c.z);
      this.fill.setX(i, bar.fill);
      this.chip.setX(i, bar.chip);
      this.alpha.setX(i, bar.alpha);
      this.tmp.set(barTone(c.type, c.state === "preparando" || c.state === "golpeando"));
      this.color.setXYZ(i, this.tmp.r, this.tmp.g, this.tmp.b);
      this.elite.setX(i, c.elite ? 1 : 0);
      i++;
    }
    for (const [id, t] of this.hitAt) if (now - t > HIT_SHOW_MS || !byId.has(id)) this.hitAt.delete(id);
    this.geometry.instanceCount = i;
    for (const a of [this.center, this.fill, this.chip, this.alpha, this.color, this.elite]) a.needsUpdate = true;
  }

  /** Barras desenhadas no último quadro (testes). */
  get visible(): number {
    return this.geometry.instanceCount;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    (this.mesh.material as { dispose(): void }).dispose();
  }
}
